
import { EMPTY, of as observableOf, Subject } from 'rxjs'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Button from '@mui/material/Button'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Tooltip from '@mui/material/Tooltip'
import { ComposerFilesState, ComposerMediaContext } from 'components/ComposerMediaContext'
import { formatDistanceToNow } from 'date-fns'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import { resetComposer, setDraft } from 'services/compose'
import { activeDraftSelector, composerAltTextErrorSelector, composerIsEmptySelector } from 'services/compose/selectors'
import { createDraft, updateDraft } from 'services/post/drafts/actions'
import { checkProductLimit } from 'services/product'
import { message } from 'services/snackbar'
import { LIMIT_POST_DRAFTS } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { MS_IN_SEC } from 'utils/constants'
import styles from './SaveDraftButton.pcss'

const AUTOSAVE_INTERVAL_SECONDS = 30

interface SaveDraftButtonProps {
  disabled?: boolean
  className?: string
}

export function SaveDraftButton(props: SaveDraftButtonProps) {
  const intl = useIntl()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { files } = useContext(ComposerMediaContext)
  const composerDraft = useSelector(activeDraftSelector)
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const withAutosave = useMemo(() => Boolean(composerDraft), [composerDraft])
  const [isSaving, setIsSaving] = useState(false)
  const isComposerEmpty = useSelector(composerIsEmptySelector)
  const altTextError = useSelector(composerAltTextErrorSelector)
  const create$ = useRef<Subject<{ files: ComposerFilesState, resetWhenComplete: boolean }>>(undefined)
  const update$ = useRef<Subject<{
    id: string
    files: ComposerFilesState
    isAutoSave: boolean
    resetWhenComplete: boolean
  }>>(undefined)
  const draftRef = useRef(composerDraft)
  useEffect(() => {
    draftRef.current = composerDraft
  }, [composerDraft])

  useEffect(() => {
    create$.current = new Subject()
    create$.current
      .pipe(
        tap(() => {
          setIsSaving(true)
        }),
        switchMap(({ files: emitFiles, resetWhenComplete }) => {
          if (draftRef.current?.id) {
            update$.current?.next({
              id: draftRef.current.id,
              files: emitFiles,
              isAutoSave: false,
              resetWhenComplete
            })
            setIsSaving(false)
            return EMPTY
          }
          return dispatch(createDraft(emitFiles))
            .pipe(
              catchError((error) => {
                return observableOf({ error })
              }),
              map((response) => {
                return { ...response, resetWhenComplete }
              })
            )
        }))
      .subscribe((response: any) => {
        setIsSaving(false)
        if (response.error) {
          if (response.error.code === 'limits-reached') {
            dispatch(checkProductLimit(LIMIT_POST_DRAFTS, response.error.data.currentCount + 1, parseInt(response.error.data.limit, 10)))
          }
          dispatch(message(intl.formatMessage({ id: 'composer.notifications.draft-save-failed' }), 'error'))
        } else {
          dispatch(message(intl.formatMessage({ id: 'composer.notifications.draft-created' }), 'success'))
          if (response.resetWhenComplete) {
            dispatch(resetComposer())
          } else if (response.draftId) {
            dispatch(setDraft({ id: response.draftId, updatedAt: new Date().toISOString() }))
          }

        }
      })

    return () => create$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    update$.current = new Subject()
    update$.current
      .pipe(
        tap(() => {
          setIsSaving(true)
        }),
        switchMap(({ id, files: emitFiles, isAutoSave, resetWhenComplete }) => dispatch(updateDraft(id, emitFiles))
          .pipe(
            map((response) => {
              return { ...response, isAutoSave, resetWhenComplete }
            }),
            catchError(() => {
              if (!isAutoSave) {
                dispatch(message(intl.formatMessage({ id: 'composer.notifications.draft-save-failed' }), 'error'))
              }
              return observableOf({ error: true })
            }))
        ))
      .subscribe((response: any) => {
        setIsSaving(false)
        if (response.error) {
          return
        }
        if (!response.isAutoSave) {
          dispatch(message(intl.formatMessage({ id: 'composer.notifications.draft-updated' }), 'success'))
        }
        if (response.resetWhenComplete) {
          dispatch(resetComposer())
        } else if (response.draftId) {
          dispatch(setDraft({ id: response.draftId, updatedAt: new Date().toISOString() }))
        }
      })

    return () => update$.current?.unsubscribe()
  }, [dispatch, intl])

  useEffect(() => {
    if (withAutosave) {
      const interval = setInterval(() => {
        if (composerDraft?.id) {
          update$.current?.next({ id: composerDraft.id, files, isAutoSave: true, resetWhenComplete: false })
        }
      }, AUTOSAVE_INTERVAL_SECONDS * MS_IN_SEC)

      return () => clearInterval(interval)
    }
    return () => null
  }, [withAutosave, composerDraft, files])

  const labelId = useMemo(() => {
    if (isSaving) {
      return 'composer.labels.draft.saving'
    }
    return 'composer.labels.draft.create'
  }, [isSaving])

  const tooltip = useMemo(() => {
    if (!composerDraft?.updatedAt) {
      return ''
    }
    return (
      <FormattedMessage
        id="composer.notifications.draft-last-update"
        values={{ timeAgo: formatDistanceToNow(composerDraft?.updatedAt, { addSuffix: true }) }}
      />
    )
  }, [composerDraft?.updatedAt])

  const save = useCallback((shouldReset: boolean) => {
    if (composerDraft?.id) {
      update$.current?.next({ id: composerDraft.id, files, isAutoSave: false, resetWhenComplete: shouldReset })
    } else {
      create$.current?.next({ files, resetWhenComplete: shouldReset })
    }
  }, [composerDraft, files])

  const handleClick = () => {
    setOpen(true)
  }

  const closePopup = () => {
    setOpen(false)
  }

  const saveAndContinue = () => {
    save(false)
    setOpen(false)
  }

  const saveAndReset = () => {
    save(true)
    setOpen(false)
  }

  return (
    <div>
      <Tooltip title={tooltip}>
        <Button
          variant="text"
          size="small"
          ref={anchorRef}
          endIcon={<ArrowDropDownIcon />}
          disabled={isComposerEmpty || isSaving || altTextError || props.disabled}
          className={`${styles.btn} ${props.className}`}
          data-testid="btn-save-draft-dropdown"
          onClick={handleClick}
        >
          <FormattedMessage id={labelId} />
        </Button>
      </Tooltip>
      <Popper open={open} anchorEl={anchorRef.current} role={undefined} disablePortal className={styles.popper}>
        <Paper>
          <ClickAwayListener mouseEvent="onMouseDown" touchEvent="onTouchStart" onClickAway={closePopup}>
            <MenuList>
              <MenuItem onClick={saveAndContinue} className={styles['menu-item']}>
                Save draft and continue
              </MenuItem>
              <MenuItem onClick={saveAndReset} className={styles['menu-item']}>
                Save draft and next post
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
    </div>
  )
}

export default SaveDraftButton
